import React, { useState } from "react";

import { Icon } from "@iconify/react";
import { cn } from "lib/utils";
import { Loader2 } from "lucide-react";

import InfoExpA from "js/components/Dashboard/InfoExpA";
import TenantActionsTable from "js/components/Tenants/TenantActionSelectTable";
import { TenantReport } from "js/components/Tenants/types";
import { PaginationTable } from "js/components/types";
import { Badge } from "js/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "js/components/ui/card";
import { TablePagination } from "js/components/ui/pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";

interface TenantReportCardListPresentationProps {
  data: TenantReport[];
  isLoading: boolean;
  pagination: PaginationTable;
  onPageChange: (page: number) => void;
  showAdvisorColumn?: boolean;
}

const TenantReportCardListPresentation: React.FC<
  TenantReportCardListPresentationProps
> = ({
  data,
  isLoading,
  pagination,
  onPageChange,
  showAdvisorColumn = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const getTenantReportStatus = (status: string) => {
    switch (status) {
      case "Created":
        return "Iniciado";
      case "Payment in Progress":
        return "Pago Pendiente";
      case "In Progress":
        return "En progreso";
      case "Completed":
        return "Completado";
      default:
        return "Estado Desconocido";
    }
  };

  const openModal = (report: TenantReport) => () => {
    if (report.expediente_azul_folder_id) {
      setIsModalVisible(true);
    }
  };

  const renderTenantReportCard = (report: TenantReport) => {
    const isMoral = report.legal_type === "Legal";
    const personName =
      report.person?.legal_representative ?? report.person?.full_name;

    return (
      <Card key={report.id} className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center">
                      <Icon
                        icon={
                          isMoral
                            ? "octicon:organization-24"
                            : "octicon:person-24"
                        }
                        fontSize={24}
                        className="text-slate-600"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {isMoral ? "Persona Moral" : "Persona Física"}
                  </TooltipContent>
                </Tooltip>
                <Badge
                  variant="outline"
                  className={cn(
                    "border-emerald-200 text-emerald-600",
                    isMoral && "border-sky-200 text-sky-600",
                  )}
                >
                  {isMoral ? "Moral" : "Física"}
                </Badge>
              </div>
              <CardTitle className="text-base leading-tight">
                {personName || "Sin nombre"}
              </CardTitle>
              <p className="text-xs text-slate-500">
                {report.created_at
                  ? new Date(report.created_at).toLocaleDateString("es-ES", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : ""}
              </p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center">
                  <Icon
                    icon={
                      report.is_active
                        ? "ion:checkmark-circle"
                        : "ion:close-circle"
                    }
                    fontSize={20}
                    color={report.is_active ? "#10b981" : "#d4d4d8"}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                {report.is_active ? "Activo" : "Inactivo"}
              </TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>

        <CardContent className="space-y-2.5 text-sm">
          {showAdvisorColumn && (
            <div className="flex justify-between">
              <span className="font-medium text-slate-600">Asesor:</span>
              <span className="text-slate-900">
                {report.advisor?.full_name?.trim() || "N/A"}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="font-medium text-slate-600">Folio:</span>
            <span className="text-slate-900">
              {report.expediente_azul_folder_id ||
                report.multiexpediente_folio ||
                "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-slate-600">Correo:</span>
            <span className="line-clamp-1 text-right text-slate-900">
              {report.person?.email || "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-slate-600">Fase:</span>
            <span className="text-slate-900">
              {getTenantReportStatus(report.status)}
            </span>
          </div>
        </CardContent>

        <CardFooter className="pt-3">
          <TenantActionsTable
            report={report}
            openModal={openModal(report)}
            showText={true}
          />
        </CardFooter>
      </Card>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 className="size-6 animate-spin" />
            <span>Cargando reportes...</span>
          </div>
        ) : data && data.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            No se encontraron reportes.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {data?.map((report) => renderTenantReportCard(report))}
            </div>
            <TablePagination
              pagination={pagination}
              onChange={onPageChange}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
      <InfoExpA
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
      />
    </>
  );
};

export default TenantReportCardListPresentation;
